import { useState } from "react"
import { Link, Outlet, useRouterState } from "@tanstack/react-router"
import { useAuthActions, useConvexAuth } from "@convex-dev/auth/react"
import { useQuery } from "convex/react"
import {
	LayoutDashboard,
	Building2,
	ListFilter,
	ContactRound,
	Send,
	CalendarClock,
	GitBranch,
	ChartNoAxesCombined,
	Radio,
	FileCode2,
	Settings,
	LogOut,
	Menu,
	Image,
} from "lucide-react"
import { api } from "../../convex/_generated/api"
import { Button } from "@/components/ui/button"
import { Loading, useTask } from "@/components/common"
import { LoginPage } from "@/pages/login"

const nav = [
	{ label: "Overview", path: "/manage", icon: LayoutDashboard },
	{ group: "Audience", label: "Businesses", path: "/manage/businesses", icon: Building2 },
	{ label: "Segments", path: "/manage/segments", icon: ListFilter },
	{ label: "Subscriptions", path: "/manage/subscriptions", icon: ContactRound },
	{ group: "Sending", label: "Campaigns", path: "/manage/campaigns", icon: Send },
	{ label: "Reminders", path: "/manage/reminders", icon: CalendarClock },
	{ label: "Flows", path: "/manage/flows", icon: GitBranch },
	{ group: "Workspace", label: "Insights", path: "/manage/insights", icon: ChartNoAxesCombined },
	{ label: "Delivery", path: "/manage/delivery", icon: Radio },
	{ label: "Templates", path: "/manage/templates", icon: FileCode2 },
	{ label: "Assets", path: "/manage/assets", icon: Image },
	{ label: "Settings", path: "/manage/settings", icon: Settings },
]

export function ProtectedLayout() {
	const auth = useConvexAuth()
	if (auth.isLoading)
		return (
			<div className="auth-page">
				<Loading />
			</div>
		)
	if (!auth.isAuthenticated) return <LoginPage />
	return <AppShell />
}
function AppShell() {
	const path = useRouterState({ select: (s) => s.location.pathname })
	const [open, setOpen] = useState(false)
	const user = useQuery(api.auth.currentUser)
	const { signOut } = useAuthActions()
	const task = useTask()
	const active = nav.find((item) =>
		item.path === "/manage"
			? path === "/manage" || path === "/manage/"
			: path.startsWith(item.path),
	)
	return (
		<div className="app-shell">
			{open && (
				<button
					aria-label="Close navigation"
					className="mobile-backdrop"
					onClick={() => setOpen(false)}
				/>
			)}
			<aside id="dashboard-navigation" className={`sidebar ${open ? "open" : ""}`}>
				<Link to="/manage" className="brand" onClick={() => setOpen(false)}>
					<img src="/logo-black.png" alt="Sumly" />
				</Link>
				<nav className="sidebar-nav" aria-label="Main navigation">
					{nav.map((item) => (
						<div key={item.path}>
							{item.group && <div className="nav-group">{item.group}</div>}
							<Link
								to={item.path}
								className={`nav-link ${active?.path === item.path ? "active" : ""}`}
								onClick={() => setOpen(false)}
							>
								<item.icon />
								{item.label}
							</Link>
						</div>
					))}
				</nav>
				<div className="mt-5 border-t pt-4">
					<div className="flex items-center gap-3 px-2">
						<div className="grid size-8 shrink-0 place-items-center rounded-full bg-[#f5e8da] text-xs font-semibold text-[#93633a]">
							{(user?.name ?? user?.email ?? "S").slice(0, 1).toUpperCase()}
						</div>
						<div className="min-w-0 flex-1">
							<div className="truncate text-xs font-semibold">{user?.name ?? "Sumly team"}</div>
							<div className="muted truncate text-[10px]">{user?.email}</div>
						</div>
						<Button
							variant="ghost"
							size="icon-sm"
							aria-label="Sign out"
							disabled={task.pending}
							onClick={() => {
								void task.run(() => signOut())
							}}
						>
							<LogOut size={15} />
						</Button>
					</div>
				</div>
			</aside>
			<div className="app-content">
				<main id="main" className="page">
					<Button
						className="mobile-menu"
						variant="ghost"
						size="icon"
						aria-label="Open navigation"
						aria-controls="dashboard-navigation"
						aria-expanded={open}
						onClick={() => setOpen(true)}
					>
						<Menu size={19} />
					</Button>
					<Outlet />
				</main>
			</div>
		</div>
	)
}
